/**
 * 데이터베이스 API 클라이언트
 * Spring Boot 백엔드에서 데이터를 가져오는 기능
 */

import apiClient from '../api/apiClient'
import { AxiosError } from 'axios'

export interface CustomerData {
  id: number;
  customerName: string;
  tenancyName: string;
  displayName: string;
  contractAmount?: number;
  contractDate?: string;
  expiryDate?: string;
  balanceExhaustionDate?: string;
  csi?: string;
  opportunityId?: string;
  subscriptionPlanNumber?: string;
  salesRepresentative?: string;
  cloudAccountManager?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DailyBalanceData {
  id: number;
  customerId: number;
  balanceDate: string;
  dailyUsage: number;
  remainingBalance: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

export interface UsageData {
  id: number;
  customerId: number;
  serviceId: number;
  serviceName: string; // 서비스명 추가
  usageDate: string;
  usageAmount: number;
  cost: number;
  currency: string;
  region?: string;
  compartmentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceData {
  id: number;
  serviceName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerUsageData {
  customerInfo: {
    고객명: string;
    테넌시명: string;
    생성일시: string;
    계약금액: number;
    계약일: string;
    만료일: string;
    잔액소진예상일: string;
    CSI: string;
    Opportunity_ID: string;
    Subscription_Plan_Number: string;
    영업대표: string;
    클라우드계정관리자: string;
  };
  dailyUsageData: Array<{
    날짜: string;
    사용량: number;
    잔액: number;
  }>;
  serviceUsageData: Array<{
    날짜: string;
    사용량: number;
    잔액: number;
    [key: string]: any;
  }>;
}

class DatabaseClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // 환경 변수에서 API URL 가져오기, 없으면 기본값 사용
    const apiUrl = process.env.REACT_APP_API_URL || '';
    this.baseUrl = baseUrl || `${apiUrl}/api`;
  }

  /**
   * 모든 고객 목록 조회
   */
  async getCustomers(): Promise<CustomerData[]> {
    try {
      const response = await apiClient.get('/customers')
      return response.data
    } catch (error) {
      console.error('고객 목록 조회 오류:', error)
      if (error instanceof AxiosError) {
        console.error('Error details:', error.response?.data)
      }
      return []
    }
  }

  /**
   * 고객 목록 조회 (한글명으로 반환)
   */
  async getCustomerList(): Promise<string[]> {
    try {
      console.log('고객 목록 조회 시작...');
      const customers = await this.getCustomers();
      console.log('받은 고객 데이터:', customers);
      const displayNames = customers.map(customer => customer.displayName);
      console.log('고객 표시명 목록:', displayNames);
      return displayNames;
    } catch (error) {
      console.error('고객 목록 조회 오류:', error);
      return [];
    }
  }

  /**
   * 고객명으로 고객 데이터 조회 (customerName 사용)
   */
  async getCustomerDataByDisplayName(displayName: string): Promise<CustomerUsageData | null> {
    try {
      const customers = await this.getCustomers();
      const customer = customers.find(c => c.displayName === displayName);

      if (!customer) {
        console.error('고객을 찾을 수 없습니다:', displayName);
        return null;
      }

      return this.getCustomerData(customer.customerName);
    } catch (error) {
      console.error('고객 데이터 조회 오류:', error);
      return null;
    }
  }

  /**
   * 특정 고객의 잔액 데이터 조회
   */
  async getCustomerBalanceData(customerName: string): Promise<DailyBalanceData[]> {
    try {
      const response = await apiClient.get(`/customers/${encodeURIComponent(customerName)}/balance`)
      return response.data
    } catch (error) {
      console.error('잔액 데이터 조회 오류:', error)
      if (error instanceof AxiosError) {
        console.error('Error details:', error.response?.data)
      }
      return []
    }
  }

  /**
   * 특정 고객의 사용량 데이터 조회
   */
  async getCustomerUsageData(customerName: string): Promise<UsageData[]> {
    try {
      const response = await apiClient.get(`/customers/${encodeURIComponent(customerName)}/usage`)
      return response.data
    } catch (error) {
      console.error('사용량 데이터 조회 오류:', error)
      if (error instanceof AxiosError) {
        console.error('Error details:', error.response?.data)
      }
      return []
    }
  }

  /**
   * 특정 고객의 집계된 사용량 데이터 조회
   */
  async getCustomerAggregatedUsageData(customerName: string): Promise<any[]> {
    try {
      const response = await apiClient.get(`/customers/${encodeURIComponent(customerName)}/usage/aggregated`)
      return response.data
    } catch (error) {
      console.error('집계된 사용량 데이터 조회 오류:', error)
      if (error instanceof AxiosError) {
        console.error('Error details:', error.response?.data)
      }
      return []
    }
  }

  /**
   * 특정 고객의 통합 데이터 조회 (백엔드 집계 API 사용)
   */
  async getCustomerData(customerName: string): Promise<CustomerUsageData | null> {
    try {
      const [balanceData, aggregatedUsageData] = await Promise.all([
        this.getCustomerBalanceData(customerName),
        this.getCustomerAggregatedUsageData(customerName)
      ]);

      // 날짜 포맷 함수
      const formatDate = (dateStr: any) => {
        // null이나 undefined인 경우
        if (!dateStr) {
          return '';
        }

        // 문자열이 아닌 경우 문자열로 변환
        const str = String(dateStr);

        // ISO 문자열에서 날짜 부분만 추출 (YYYY-MM-DD 형식)
        if (str.includes('T')) {
          return str.split('T')[0];
        }
        // 이미 YYYY-MM-DD 형식인 경우
        if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return str;
        }
        // 다른 형식인 경우 Date 객체로 변환
        const date = new Date(str);
        if (isNaN(date.getTime())) {
          return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // 집계된 사용량 데이터를 프론트엔드 형식으로 변환
      const dailyUsageData = aggregatedUsageData.map((item: any) => ({
        날짜: formatDate(item.date),
        사용량: Number(item.totalUsage) || 0,
        잔액: Number(item.remainingBalance) || 0
      }));

      // 잔액 예상 소진일 가져오기 (첫 번째 항목에서)
      const balanceExhaustionDate = aggregatedUsageData.length > 0
        ? aggregatedUsageData[0].balanceExhaustionDate || ''
        : '';

      // 서비스별 사용량 데이터 생성
      const serviceUsageData = aggregatedUsageData.map((item: any) => {
        const serviceData: any = {
          날짜: formatDate(item.date),
          사용량: Number(item.totalUsage) || 0,
          잔액: Number(item.remainingBalance) || 0
        };

        // 서비스별 사용량 추가
        if (item.serviceUsage) {
          Object.entries(item.serviceUsage).forEach(([serviceName, cost]: [string, any]) => {
            serviceData[serviceName] = Number(cost) || 0;
          });
        }

        return serviceData;
      });

      // 실제 고객 정보 가져오기
      const customers = await this.getCustomers();
      const customer = customers.find(c => c.customerName === customerName);

      const customerInfo = {
        고객명: customer?.displayName || customerName,
        테넌시명: customer?.tenancyName || customerName,
        생성일시: customer?.createdAt ? formatDate(customer.createdAt) : formatDate(new Date().toISOString()),
        계약금액: customer?.contractAmount || 0,
        계약일: customer?.contractDate || '',
        만료일: customer?.expiryDate || '',
        잔액소진예상일: balanceExhaustionDate,
        CSI: customer?.csi || '',
        Opportunity_ID: customer?.opportunityId || '',
        Subscription_Plan_Number: customer?.subscriptionPlanNumber || '',
        영업대표: customer?.salesRepresentative || '',
        클라우드계정관리자: customer?.cloudAccountManager || ''
      };

      return {
        customerInfo,
        dailyUsageData,
        serviceUsageData
      };
    } catch (error) {
      console.error('고객 데이터 조회 오류:', error);
      return null;
    }
  }

  /**
   * 모든 고객의 데이터 조회
   */
  async getAllCustomerData(): Promise<Record<string, CustomerUsageData>> {
    try {
      const customers = await this.getCustomerList();
      const allData: Record<string, CustomerUsageData> = {};

      for (const customerName of customers) {
        const data = await this.getCustomerData(customerName);
        if (data) {
          allData[customerName] = data;
        }
      }

      return allData;
    } catch (error) {
      console.error('모든 고객 데이터 조회 오류:', error);
      return {};
    }
  }

  /**
   * 서버 상태 확인
   */
  async healthCheck(): Promise<boolean> {
    try {
      await apiClient.get('/data/health')
      return true
    } catch (error) {
      console.error('서버 상태 확인 오류:', error)
      if (error instanceof AxiosError) {
        console.error('Error details:', error.response?.data)
      }
      return false
    }
  }
}

export default DatabaseClient;
